// RootRoute — porta de entrada do app TV (I5+I6).
//
// Extraído de routes.tsx pra ser testável em isolamento: routes.tsx chama
// createBrowserRouter no top-level (precisa de window.history), o que quebraria
// um teste rodando no ambiente `node`. RootRoute é decisão síncrona pura.

import { Navigate } from 'react-router-dom'

import { MobileRoot } from './MobileRoot'
import { AccountGateScreen } from './screens/AccountGateScreen'
import { WelcomeScreen } from './screens/WelcomeScreen'
import { useSessionStore } from './store'
import { resolveSurface } from './surface'
import { hasSwipeSession } from './sync'
import { hasSeenWelcome } from './welcome-flag'

/**
 * I5+I6: a CONTA SWIPE é obrigatória pra tudo — a decisão de roteamento gira em
 * torno de `hasSwipeSession()`, não mais do token Xtream anônimo. O caminho
 * "Continuar sem conta" saiu do boot (spec onboarding-entrada-tv-ux §3):
 * `/onboarding/mode`, `/code` e `/direct` continuam existindo como CONFIG INTERNA
 * (reconfigurar fonte logado), nunca como porta de entrada.
 *
 * Árvore de decisão (ordem importa — do mais "completo" pro gate):
 *  1. isAuthed (fonte resolvida) + perfil → /home; sem perfil → /profiles.
 *  2. isAuthed SEM sessão Swipe = BASE INSTALADA (Xtream legado) → GATE DE
 *     VÍNCULO. NÃO desloga: ao parear, o sync migra os dados pra conta. (I6)
 *  3. Sessão Swipe SEM fonte ativa (pareou mas no_provider) → escolher/config
 *     fonte PÓS-conta (reusa /onboarding/mode|/login|/direct, agora logado —
 *     não é o caminho anônimo).
 *  4. Sem sessão Swipe + Welcome nunca vista → SLIDER (que leva ao gate).
 *  5. Sem sessão Swipe + Welcome já vista (2º+ boot) → GATE DE ENTRADA direto.
 */
export function RootRoute() {
  // A0 (frente mobile) — BIFURCAÇÃO de superfície no TOPO da porta de entrada.
  // Em celular/tablet (touch + viewport estreito, NÃO-TV) renderiza a superfície
  // mobile. Em TV cai no `TvRoot` abaixo — caminho de hoje, IDÊNTICO
  // (resolveSurface é conservador: na dúvida, 'tv'). resolveSurface() é estável
  // por sessão (decide uma vez no boot), então não fica oscilando com
  // resize/rotação. Separamos o corpo TV em `TvRoot` pra não chamar os hooks de
  // sessão condicionalmente (rules-of-hooks) — a superfície mobile não monta
  // nenhum hook da árvore TV.
  if (resolveSurface() === 'mobile') return <MobileRoot />
  return <TvRoot />
}

/** Caminho TV — corpo histórico do RootRoute, INTACTO. Só roda quando a
 *  superfície é 'tv' (todas as TVs e o desktop/web). */
function TvRoot() {
  const {
    mode,
    providerCode,
    sourceType,
    isAuthed,
    direct,
    activeProfileId,
    pairingFinalizing,
  } = useSessionStore()
  const swipe = hasSwipeSession()

  // 0: PAREAMENTO QR EM CURSO — mantém o gate montado, ANTES de qualquer decisão
  // de rota (fix empacotado file://). O gate (AccountGateScreen) hospeda o
  // `usePairingFlow`; QUALQUER redirect por baixo dele o desmonta e aborta o
  // pairing no meio. Durante o `finalizeLogin` o store muda em passos
  // (loginSwipeSession → setProvider → api.login → setAuth), cada um disparando
  // um re-render deste componente ENQUANTO isAuthed ainda não virou true. Sem
  // esta guarda no topo, esses re-renders caíam no caso 1 (base instalada, já
  // isAuthed) ou no caso 3 (fresh install, swipe sem isAuthed) e navegavam pra
  // fora — o sintoma reportado: após o QR aprovado a TV caía na tela de
  // user/senha Xtream. O flag só está ligado durante um pairing ativo (finalize
  // ou needsPreference/error aguardando o usuário); é desligado no setAuth
  // (sucesso), no cancel/configureHere/retry (usuário sai/reinicia) e no
  // logout/reset. Genérico (web e file://); NÃO concede acesso — o app só entra
  // via caso 1 (isAuthed=true, exige api.login validado no backend).
  if (pairingFinalizing) return <AccountGateScreen />

  // 1+2: fonte Xtream resolvida (isAuthed=true).
  if (isAuthed) {
    // BASE INSTALADA (I6): logado só com Xtream, sem conta Swipe → gate de
    // vínculo. AccountGateScreen detecta a variante e mostra copy acolhedora;
    // não deslogamos nem apagamos nada — o pareamento migra via sync.
    if (!swipe) return <AccountGateScreen />
    if (!activeProfileId) return <Navigate to="/profiles" replace />
    return <Navigate to="/home" replace />
  }

  // 3: COM sessão Swipe mas SEM fonte resolvida (pareou; backend devolve
  // no_provider). Conduz a configurar a fonte — PÓS-conta, logado. Reusa as
  // telas internas (mode preservado de sessão anterior pula o seletor).
  if (swipe) {
    // Fonte MANUAL (lista pessoal da conta) vai pra a tela dela, não pro /login.
    // As duas chegam aqui com `mode:'provider'` + `providerCode`, porque o login
    // Xtream da lista pessoal também usa um código — só que o EFÊMERO (`D{hash}`),
    // gerado pelo servidor. Enquanto o RootRoute só via "tem código", ele mandava
    // a lista pessoal pro formulário de código; foi essa cegueira que o relogin
    // transformou em ESCRITA (a fonte manual era regravada como fonte por código,
    // apagando a URL da conta em todos os aparelhos). Agora o gatilho é o
    // INVARIANTE (`sourceType`), e cada fonte volta pra a tela que sabe tratá-la.
    if (mode === 'provider' && providerCode && sourceType === 'manual') {
      return <Navigate to="/source/manual" replace />
    }
    if (mode === 'provider' && providerCode) return <Navigate to="/login" replace />
    if (mode === 'direct' && direct) return <Navigate to="/direct" replace />
    return <Navigate to="/onboarding/mode" replace />
  }

  // 4: 1ª abertura ou hard reset (welcome-seen=false) → slider de onboarding.
  if (!hasSeenWelcome()) return <WelcomeScreen />

  // 5: 2º+ boot sem sessão (viu o slider, não pareou) → gate direto.
  return <AccountGateScreen />
}
